import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useContent } from '../content/ContentContext';
import { Reveal, Kicker } from '../components/Reveal';
import { Marquee } from '../components/Marquee';
import { SEO } from '../components/SEO';

export const IMG = {
  hero: '/images/foto-progetto.jpg',
  cooking: '/images/cooking.jpg',
  foodart: '/images/foodart.jpg',
  wine: '/images/wine-beer.jpg',
  space: '/images/5v9a4740.jpg',
  mixology: '/images/mixology.jpg',
  evento: '/images/evento.jpg',
  catering: '/images/catering.jpg',
  eventoNotte: '/images/evento-notte.jpg',
  cantina: '/images/cantina.jpg',
  gnocchi: '/images/gnocchi.jpg',
  burrata: '/images/burrata.jpg',
  aperitivo: '/images/aperitivo.jpg',
  archi: '/images/archi.jpg',
  gruppo: '/images/gruppo-cooking.jpg',
  chef: '/images/dsc06725.jpg',
  aboutGraphic: '/images/chi-siamo.jpg',
  locationNew: '/images/location-nuova.jpg',
  promozione: '/images/promozione.jpg',
  cookingNew: '/images/cooking-nuova.jpg',
  locationEvento: '/images/location-evento.jpg',
  pianoTerra: '/images/piano-terra.jpg',
  burrataCup: '/images/burrata-cup.jpg',
};

export const PARTNER_LINKS = {
  'Gentile since 1950': 'https://gentile1950.com',
  'Midora Atelier Gastronomico': 'https://www.midoraatelier.com',
  'F.lli Abrigo · Cascina Langairolo': 'https://www.cascinalangairolo.it',
  'Casa Bugatti': 'https://www.casabugatti.it',
  'Alma Design': 'https://www.almadesign.it',
};

export const Banner = ({ title, text, cta }) => (
  <section className="bg-emerald grain relative" data-testid="cta-banner">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <Reveal>
        <h2 className="font-serif text-4xl sm:text-5xl text-ivory tracking-tight max-w-2xl">{title}</h2>
        <p className="mt-5 text-ivory/70 max-w-lg text-base md:text-lg">{text}</p>
        <Link
          to="/contatti"
          data-testid="banner-cta"
          className="mt-10 inline-flex items-center gap-3 rounded-full bg-terracotta text-emerald-ink px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-ivory hover:-translate-y-0.5"
        >
          {cta} <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const { t, lang } = useLanguage();
  const { img, ct } = useContent();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 220]);
  const heroScale = useTransform(scrollY, [0, 900], [1.05, 1.2]);

  const heroLines = [ct('hero_line1', t.home.heroLines[0]), ct('hero_line2', t.home.heroLines[1]), ct('hero_line3', t.home.heroLines[2])];
  const chapterImgs = [img('home-ch1', IMG.aboutGraphic), img('home-ch2', IMG.burrataCup), img('home-ch3', IMG.locationNew)];
  const serviceImgs = [img('serv-0', IMG.cookingNew), img('serv-2', IMG.mixology), img('serv-3', IMG.pianoTerra), img('serv-5', IMG.catering)];
  const galleryImgs = [IMG.gnocchi, IMG.burrata, IMG.aperitivo, IMG.archi, IMG.eventoNotte, IMG.locationEvento];

  return (
    <div data-testid="home-page">
      <SEO title={t.meta.homeTitle} description={t.meta.homeDesc} />

      <section className="relative min-h-[100svh] flex items-end overflow-hidden bg-emerald-deep" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <img src={img('home-hero', IMG.hero)} alt="Location Gustavo 28 Milano" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-emerald-deep/70" />
        </motion.div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-28 pt-40 w-full grain">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="text-xs uppercase tracking-[0.35em] text-terracotta mb-8"
            data-testid="hero-kicker"
          >
            {t.home.kicker}
          </motion.p>
          <h1 className="font-serif text-ivory text-5xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight" data-testid="hero-title">
            {heroLines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? 'italic text-terracotta' : ''}`}
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-ivory/80 text-base md:text-lg"
            data-testid="hero-subtitle"
          >
            {ct('hero_sub', t.home.heroSub)}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-3 rounded-full bg-terracotta text-emerald-ink px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-ivory hover:-translate-y-0.5"
            >
              {t.home.ctaPrimary} <ArrowRight size={16} />
            </Link>
            <Link
              to="/location"
              data-testid="hero-cta-location"
              className="inline-flex items-center gap-3 rounded-full border border-ivory/40 text-ivory px-8 py-4 text-sm tracking-wide transition-all duration-300 hover:border-terracotta hover:text-terracotta hover:-translate-y-0.5"
            >
              {t.home.ctaSecondary}
            </Link>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-8 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-ivory/60"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center translate-y-[-6px]">{t.home.scroll}</span>
          <ArrowDown size={16} className="animate-bounce mt-4" />
        </motion.div>
      </section>

      <Marquee items={t.home.marquee} />

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36" data-testid="manifesto">
        <Reveal>
          <Kicker>{t.home.chaptersKicker}</Kicker>
        </Reveal>
        <div className="mt-12 space-y-28 md:space-y-36">
          {t.home.chapters.map((ch, i) => (
            <div key={ch.title} className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
              <div className={`md:col-span-6 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                <Reveal>
                  <span className="font-serif italic text-7xl md:text-8xl text-terracotta/70 leading-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-emerald tracking-tight">{ch.title}</h2>
                  <p className="mt-6 text-emerald-ink/70 text-base md:text-lg leading-relaxed max-w-lg">{ch.text}</p>
                  {ch.cta && (
                    <Link
                      to="/location"
                      data-testid="manifesto-cta-location"
                      className="mt-8 inline-flex items-center gap-3 text-sm font-medium text-terracotta-dark tracking-wide group"
                    >
                      {ch.cta}
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                </Reveal>
              </div>
              <div className={`md:col-span-6 ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <Reveal delay={0.15}>
                  <div className="img-frame rounded-sm shadow-[0_8px_40px_rgba(37,79,87,0.15)]">
                    <img src={chapterImgs[i]} alt={ch.title} className={`w-full aspect-[4/3] ${i === 0 ? 'object-contain bg-emerald' : 'object-cover object-center'}`} loading="lazy" />
                  </div>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ivory-soft border-y border-emerald/10" data-testid="services-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Kicker>{t.home.servicesKicker}</Kicker>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-emerald tracking-tight">{t.home.servicesTitle}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
            {t.home.services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className={i % 2 === 0 ? 'md:col-span-7' : 'md:col-span-5'}>
                <Link
                  to="/servizi"
                  data-testid={`service-card-${i}`}
                  className="group block bg-white border border-emerald/10 rounded-sm overflow-hidden transition-all duration-500 hover:shadow-[0_12px_50px_rgba(37,79,87,0.14)] hover:-translate-y-1"
                >
                  <div className="img-frame">
                    <img src={serviceImgs[i]} alt={s.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                  </div>
                  <div className="p-8">
                    <h3 className="font-serif text-2xl text-emerald group-hover:text-terracotta-dark transition-colors duration-300">{s.title}</h3>
                    <p className="mt-3 text-sm text-emerald-ink/60 leading-relaxed">{s.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link
              to="/servizi"
              data-testid="services-cta-all"
              className="inline-flex items-center gap-3 rounded-full bg-emerald text-ivory px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5"
            >
              {t.home.servicesCta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="gallery">
        <Reveal>
          <Kicker>{t.home.galleryKicker}</Kicker>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-emerald tracking-tight">{t.home.galleryTitle}</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {galleryImgs.map((g, i) => (
            <Reveal key={g} delay={(i % 3) * 0.08}>
              <div className="img-frame rounded-sm shadow-[0_8px_30px_rgba(37,79,87,0.10)]">
                <img src={g} alt="Gustavo 28 — evento" className="w-full aspect-square object-cover" loading="lazy" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <Link
            to="/gallery"
            data-testid="gallery-cta-all"
            className="inline-flex items-center gap-3 rounded-full bg-emerald text-ivory px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5"
          >
            {t.home.galleryCta} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="partners">
        <Reveal>
          <Kicker>{t.home.partnersKicker}</Kicker>
        </Reveal>
        <div className="mt-10 flex flex-wrap items-baseline gap-x-12 gap-y-6">
          {t.home.partners.map((p, i) => (
            <Reveal key={p} delay={i * 0.06}>
              {PARTNER_LINKS[p] ? (
                <a
                  href={PARTNER_LINKS[p]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-link-${i}`}
                  className="font-serif text-xl md:text-2xl text-emerald/70 hover:text-terracotta-dark transition-colors duration-300 underline-offset-4 hover:underline"
                >
                  {p}
                </a>
              ) : (
                <span className="font-serif text-xl md:text-2xl text-emerald/70">{p}</span>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <Banner title={t.home.bannerTitle} text={t.home.bannerText} cta={t.home.bannerCta} />
    </div>
  );
}
